'use client';

/**
 * @fileoverview URL-state aware client wrapper around `SubGenreChips`
 * (WEB-128). Reads `?genre=` from `useSearchParams`, derives the active
 * slug, and writes selections back through `router.replace` so the back
 * button does not fill with intermediate states. The presentational
 * tablist (`SubGenreChips`) stays pure — this wrapper is the only place
 * that touches the App Router.
 *
 * Selecting "All" strips `?genre=` entirely; selecting a sub-genre sets
 * it to the chip's slug. Unrelated query params (e.g. `?date=tonight`,
 * UTM tags) are preserved by mutating a copy of the current
 * `URLSearchParams` instead of rebuilding the query from scratch.
 *
 * `useTransition` keeps the previous shelves visible while the RSC
 * boundary refetches, which is what stops layout shift between renders.
 *
 * @since WEB-128
 */
import * as React from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { SubGenreChips, type SubGenreChipsItem, type SubGenreChipsProps } from './SubGenreChips';

export interface SubGenreChipsConnectedProps extends Pick<
  SubGenreChipsProps,
  'label' | 'className' | 'totalCount'
> {
  items: SubGenreChipsItem[];
}

/** Query-string key used to persist the selected sub-genre. */
const GENRE_PARAM = 'genre';
/**
 * Legacy multi-value query-string key (`?genres=indie,jazz`). The canonical
 * single-value `?genre=` param wins everywhere, but we still read `?genres=`
 * as a fallback so deep links cut from older builds preselect correctly.
 * On any chip change we strip the legacy param so the URL canonicalizes
 * back to `?genre=` and stale filters cannot leak forward.
 */
const LEGACY_GENRES_PARAM = 'genres';

/**
 * Resolve the chip selection from URL state. Prefers canonical `?genre=`;
 * if absent, picks the first non-empty slug from `?genres=` (comma-split).
 * Returns `null` when neither is present, which renders "All" as selected.
 */
function resolveSelectedSlug(searchParams: URLSearchParams): string | null {
  const canonical = searchParams.get(GENRE_PARAM);
  if (canonical !== null && canonical.length > 0) return canonical;
  const legacy = searchParams.get(LEGACY_GENRES_PARAM);
  if (legacy === null) return null;
  const firstSlug = legacy
    .split(',')
    .map((slug) => slug.trim())
    .find((slug) => slug.length > 0);
  return firstSlug ?? null;
}

export function SubGenreChipsConnected({
  items,
  label,
  className,
  totalCount,
}: SubGenreChipsConnectedProps): React.ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, startTransition] = React.useTransition();

  const selectedSlug = resolveSelectedSlug(searchParams);

  const handleChange = React.useCallback(
    (next: string | null): void => {
      const params = new URLSearchParams(searchParams.toString());
      // Always strip the legacy multi-value param on any chip change so
      // selections never leave stale `?genres=` filters in the URL.
      params.delete(LEGACY_GENRES_PARAM);
      if (next === null) {
        params.delete(GENRE_PARAM);
      } else {
        params.set(GENRE_PARAM, next);
      }
      const queryString = params.toString();
      const href = queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
      startTransition(() => {
        router.replace(href, { scroll: false });
      });
    },
    [pathname, router, searchParams],
  );

  return (
    <SubGenreChips
      items={items}
      selectedSlug={selectedSlug}
      onChange={handleChange}
      label={label}
      totalCount={totalCount}
      className={className}
    />
  );
}
